'use client'

import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { cn } from '@tebuto/lib/utils'
import { CheckoutButton } from '@tebuto/payment/checkout/CheckoutButton'
import { Banknote, ReceiptText } from 'lucide-react'
import type { ReactNode } from 'react'
import { PayPalOfflineMethodRow } from './PayPalOfflineMethodRow'
import type { WidgetPaymentMethodId, WidgetPaymentOption } from './payment-option.types'

export function resolvePayPalOnlineSectionVisibility({
    hasOnlinePayment,
    onlinePaymentMethodCount,
    selectedOfflineOption
}: {
    hasOnlinePayment: boolean
    onlinePaymentMethodCount: number
    selectedOfflineOption: WidgetPaymentOption | null
}): { hasOnlineMethods: boolean; showFullOnlineSection: boolean; showOnlineSwitch: boolean } {
    const hasOnlineMethods = hasOnlinePayment && onlinePaymentMethodCount > 0
    return {
        hasOnlineMethods,
        showFullOnlineSection: hasOnlineMethods && selectedOfflineOption === null,
        showOnlineSwitch: hasOnlineMethods && selectedOfflineOption !== null
    }
}

interface PayPalPaymentMethodsBodyProps {
    paymentOptions: WidgetPaymentOption[]
    selectedMethodId: WidgetPaymentMethodId | null
    onSelectOfflineMethod: (methodId: WidgetPaymentMethodId) => void
    processingMethodId: WidgetPaymentMethodId | null
    isOnlineProcessing: boolean
    hasOnlinePayment: boolean
    onlinePaymentMethodCount: number
    onlinePaymentSlot: ReactNode
    selectedOfflineOption: WidgetPaymentOption | null
    onBottomCtaClick: () => void
    bottomCtaDisabled: boolean
    bottomCtaLoading: boolean
    bottomCtaLabel: string
    onShowOnlinePayment: () => void
}

export function PayPalPaymentMethodsBody({
    paymentOptions,
    selectedMethodId,
    onSelectOfflineMethod,
    processingMethodId,
    isOnlineProcessing,
    hasOnlinePayment,
    onlinePaymentMethodCount,
    onlinePaymentSlot,
    selectedOfflineOption,
    onBottomCtaClick,
    bottomCtaDisabled,
    bottomCtaLoading,
    bottomCtaLabel,
    onShowOnlinePayment
}: PayPalPaymentMethodsBodyProps) {
    const { t } = useComponentTranslation()

    const { hasOnlineMethods, showFullOnlineSection, showOnlineSwitch } = resolvePayPalOnlineSectionVisibility({
        hasOnlinePayment,
        onlinePaymentMethodCount,
        selectedOfflineOption
    })

    return (
        <div className="flex min-w-0 w-full flex-col gap-12">
            {paymentOptions.length > 0 ? (
                <section className="widget-paypal-payment-section" aria-labelledby="paypal-offline-section-title">
                    <h3 id="paypal-offline-section-title" className="widget-paypal-payment-section__title">
                        {t('booking.payment.paypalOfflineSectionTitle')}
                    </h3>
                    <div
                        className="widget-paypal-payment-section__content"
                        role="radiogroup"
                        aria-label={t('booking.payment.paypalOfflineSectionTitle')}
                    >
                        {paymentOptions.map((option) => (
                            <PayPalOfflineMethodRow
                                key={option.id}
                                label={option.label}
                                selected={selectedMethodId === option.id}
                                icon={option.id === 'cash' ? <Banknote /> : <ReceiptText />}
                                disabled={isOnlineProcessing || Boolean(processingMethodId)}
                                onSelect={() => onSelectOfflineMethod(option.id)}
                                testId={`booking-payment-${option.id}`}
                            />
                        ))}
                    </div>
                    {selectedOfflineOption ? (
                        <>
                            <CheckoutButton
                                type="button"
                                data-testid="booking-payment-submit"
                                onClick={onBottomCtaClick}
                                disabled={bottomCtaDisabled}
                                isLoading={bottomCtaLoading}
                                className="mt-4 min-h-11 text-base"
                            >
                                {bottomCtaLabel}
                            </CheckoutButton>
                            {showOnlineSwitch ? (
                                <button
                                    type="button"
                                    data-testid="booking-payment-show-online"
                                    onClick={onShowOnlinePayment}
                                    disabled={isOnlineProcessing || Boolean(processingMethodId) || bottomCtaLoading}
                                    className="mt-3 w-full text-center text-sm font-medium underline widget-text-secondary transition-colors hover:widget-text-primary disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {t('booking.payment.paypalSwitchToOnline')}
                                </button>
                            ) : null}
                        </>
                    ) : null}
                </section>
            ) : null}

            {hasOnlineMethods ? (
                <section
                    className={cn(
                        'widget-paypal-payment-section',
                        !showFullOnlineSection && 'widget-paypal-payment-section--hidden'
                    )}
                    aria-labelledby="paypal-online-section-title"
                    aria-hidden={!showFullOnlineSection}
                    hidden={!showFullOnlineSection}
                    data-testid="booking-payment-online-section"
                >
                    <h3 id="paypal-online-section-title" className="widget-paypal-payment-section__title">
                        {t('booking.payment.paypalOnlineSectionTitle')}
                    </h3>
                    <div
                        className="widget-paypal-payment-section__content widget-paypal-online-payment"
                        data-testid="booking-payment-native-online-action"
                    >
                        {onlinePaymentSlot}
                    </div>
                </section>
            ) : null}
        </div>
    )
}
